/**
 * Rhyme Category Educational Content
 * Detailed explanations, examples, and stability context for each RhymeCategory
 *
 * Used by RhymeCategoryTooltip to provide educational content in RhymePopover
 */

import { RhymeCategory } from '../utils/rhyme/types'

export interface RhymeCategoryDescription {
  /** Human-readable category name */
  name: string
  /** Detailed educational explanation */
  explanation: string
  /** Example word pairs that demonstrate this category */
  examples: Array<{ word1: string; word2: string }>
  /** Stability note template - used for matches/tension context */
  stabilityNote: {
    matchesExpectation: string
    addsTension: string
  }
}

/**
 * Educational content for all rhyme categories
 *
 * Provides:
 * - Category name (matches CATEGORY_DISPLAY)
 * - Detailed explanation of the rhyme mechanism
 * - Example word pairs demonstrating the category
 * - Stability context (matches expectation / adds tension)
 *
 * Data is preloaded (no async operations) for fast tooltip rendering.
 * Target: <5ms additional render time beyond base RhymePopover.
 */
export const RHYME_CATEGORY_EXPLANATIONS: Record<RhymeCategory, RhymeCategoryDescription> = {
  [RhymeCategory.PERFECT]: {
    name: 'Perfect Rhyme',
    explanation: 'The gold standard of rhyming - identical sounds from the stressed vowel to the end of each word. Creates the strongest sense of resolution and is what listeners traditionally expect in rhyming positions.',
    examples: [
      { word1: 'cat', word2: 'hat' },
      { word1: 'love', word2: 'dove' }
    ],
    stabilityNote: {
      matchesExpectation: 'This perfect rhyme reinforces stability - exactly what this section expects.',
      addsTension: 'Strong rhyme here adds unexpected polish, creating a more formal tone than this section typically uses.'
    }
  },
  [RhymeCategory.FAMILY_VOICED]: {
    name: 'Voiced Family Rhyme',
    explanation: 'Final consonants differ only by voicing (both are voiced - vibrating vocal cords). The vowel sounds match perfectly, creating near-perfect rhyme quality with subtle variation.',
    examples: [
      { word1: 'cab', word2: 'cad' },
      { word1: 'rave', word2: 'raise' }
    ],
    stabilityNote: {
      matchesExpectation: 'This family rhyme provides strong connection while adding subtle texture.',
      addsTension: 'The voiced consonant variation adds unexpected warmth to this rhyme position.'
    }
  },
  [RhymeCategory.FAMILY_UNVOICED]: {
    name: 'Unvoiced Family Rhyme',
    explanation: 'Final consonants differ only by voicing (both are unvoiced - no vocal cord vibration). Creates a crisp, precise near-rhyme that feels intentional.',
    examples: [
      { word1: 'cap', word2: 'cat' },
      { word1: 'miss', word2: 'myth' }
    ],
    stabilityNote: {
      matchesExpectation: 'This crisp family rhyme delivers expected resolution with character.',
      addsTension: 'The precise consonant variation creates an unexpectedly sharp edge here.'
    }
  },
  [RhymeCategory.ADDITIVE]: {
    name: 'Additive Rhyme',
    explanation: 'One word adds an extra consonant sound to the other. The core rhyme is there, with one word feeling like an "extended version" of the sound.',
    examples: [
      { word1: 'car', word2: 'card' },
      { word1: 'spin', word2: 'sprint' }
    ],
    stabilityNote: {
      matchesExpectation: 'This additive rhyme adds creative variation while maintaining connection.',
      addsTension: 'The extra sound creates forward momentum that adds energy to this position.'
    }
  },
  [RhymeCategory.SUBTRACTIVE]: {
    name: 'Subtractive Rhyme',
    explanation: 'One word removes a consonant sound from the other. Creates an echo effect where one word feels like a shortened version of the other.',
    examples: [
      { word1: 'sprint', word2: 'spin' },
      { word1: 'blend', word2: 'bend' }
    ],
    stabilityNote: {
      matchesExpectation: 'This subtractive rhyme provides satisfying connection with interesting variation.',
      addsTension: 'The reduced sound creates a lighter landing than expected in this rhyme position.'
    }
  },
  [RhymeCategory.ASSONANCE]: {
    name: 'Assonance',
    explanation: 'Matching stressed vowel sounds with different consonants. Creates a melodic connection that feels more like a musical echo than a traditional rhyme. Common in hip-hop and contemporary songwriting.',
    examples: [
      { word1: 'lake', word2: 'fade' },
      { word1: 'time', word2: 'night' }
    ],
    stabilityNote: {
      matchesExpectation: 'This vowel-based connection creates the looser, more creative feel this section calls for.',
      addsTension: 'Assonance here adds an unexpected modern, hip-hop influenced texture.'
    }
  },
  [RhymeCategory.CONSONANCE]: {
    name: 'Consonance',
    explanation: 'Matching final consonant sounds with different vowels. The shared consonants create a percussive connection, often used for emphasis or to avoid predictable rhymes.',
    examples: [
      { word1: 'black', word2: 'block' },
      { word1: 'odds', word2: 'ends' }
    ],
    stabilityNote: {
      matchesExpectation: 'This consonant-based link provides subtle connection appropriate for this section.',
      addsTension: 'The consonant echo creates an unexpected rhythmic punch in this position.'
    }
  },
  [RhymeCategory.NONE]: {
    name: 'No Rhyme',
    explanation: 'No significant sound pattern detected between these words. This could be intentional (for contrast or to avoid forced rhymes) or simply a non-rhyming line.',
    examples: [
      { word1: 'heart', word2: 'soul' },
      { word1: 'dream', word2: 'hope' }
    ],
    stabilityNote: {
      matchesExpectation: 'No rhyme here is appropriate - this section doesn\'t require rhyming.',
      addsTension: 'This non-rhyme breaks the expected pattern, creating deliberate contrast.'
    }
  }
}
